<template lang="pug">
  form.product-form(
    @submit.prevent="addToCart"
    novalidate
    :variant-id="currentVariantId"
    :option-one="this.selectedSize"
    :option-two="this.selectedGrind"
  )
    .product-form__tile-options
      subscription-select-buttons.product-form__options__option1(
        v-if="hasSubscription"
        label="Select A Purchase Option"
        v-model="selectedPurchaseType"
        :product="product"
        :disable-subscription="disableSubscription"
        :subscription-price="subscriptionPrice"
        :subscription-discount="subscriptionDiscount"
      )
    .product-form__options(
      v-if="isSubscriptionSelected"
    )
      select-input.product-form__label(
        label="Frequency"
        :options="subscriptionFrequencies"
        v-model="selectedFrequency"
        required
      )
    .product-form__options
      select-input.product-form__label(
        label="Size"
        :options="sizeOptions"
        v-model="selectedSize"
        required
      )
    .product-form__options
      select-input.product-form__label(
        label="Grind"
        :options="grindOptions"
        v-model="selectedGrind"
        required
      )
    .product-form__atc
      quantity-input(
        v-model="quantity"
        :min="1"
      )
      submit-button(
        :label="buttonLabel"
        :disabled="isDisabled"
        :loading="adding"
        full
      )
</template>

<script>
  import { mapState } from 'vuex';
  import { purchaseTypeSingle, purchaseTypeRecurring } from 'scripts/config/subscriptions.js';
  import { getDiscountPercentage } from 'scripts/helpers/product.js';
  import { getFreeShipping, getVariantsWithSubscriptions } from 'scripts/helpers/tags.js';
  import { money } from 'scripts/filters/money.js';

  import Icon from 'scripts/components/basic/Icon.vue';
  import QuantityInput from 'scripts/components/forms/QuantityInput.vue';
  import SelectInput from 'scripts/components/forms/SelectInput.vue';
  import VariantSelectButtons from 'scripts/components/product/VariantSelectButtons.vue';
  import SubscriptionSelectButtons from 'scripts/components/product/SubscriptionSelectButtons.vue';
  import SubmitButton from 'scripts/components/buttons/SubmitButton.vue';

  export default {
    name: 'ProductForm',
    components: {
      Icon,
      QuantityInput,
      SelectInput,
      VariantSelectButtons,
      SubscriptionSelectButtons,
      SubmitButton,
    },
    props: {
      product: Object,
      defaultToSubscription: {
        type: Boolean,
        default: false,
      }
    },
    data () {
      return {
        quantity: 1,
        adding: false,
        selectedPurchaseType: purchaseTypeSingle,
        selectedFrequency: '',
        selectedSize: '',
        selectedGrind: '',
        variantsWithSubscriptions: this.product ? getVariantsWithSubscriptions(this.product.tags) : [],
      };
    },
    computed: {
      ...mapState({
        variantParam: state => state.params.variant,
      }),
      isDisabled () {
        return this.adding || !this.currentVariantIsAvailable;
      },
      buttonLabel () {
        return (this.currentVariantIsAvailable)
          ? `ADD TO CART`
          : 'SOLD OUT';
      },
      hasFreeShipping () {
        return getFreeShipping(this.product.tags);
      },
      // subscription data
      hasSubscription () {
        // Check for either selling plans or legacy subscription
        return Boolean((this.product && this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0) || (this.product && this.product.subscription));
      },
      sizeOptions () {
        return this.buildOptionsArray('option1')
      },
      grindOptions () {
        return this.buildOptionsArray('option2');
      },
      isSubscriptionSelected () {
        return (this.selectedPurchaseType) === purchaseTypeRecurring;
      },
      disableSubscription () {
        return !this.product.variants.find((variant) => variant.id === this.currentVariantId);
      },
      subscriptionUnitType () {
        return (this.hasSubscription && this.product && this.product.subscription) ? this.product.subscription.shipping_interval_unit_type : '';
      },
      subscriptionFrequencies () {
        if (!this.hasSubscription || !this.product) {
          return [];
        }

        // Handle native Shopify selling plans
        if (this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0) {
          const sellingPlans = this.product.selling_plan_groups[0].selling_plans;
          return sellingPlans.map((plan) => {
            return {
              value: plan.id.toString(),
              label: plan.name,
            };
          });
        }

        // Handle legacy subscription
        if (this.product.subscription) {
          return this.product.subscription.shipping_interval_frequency.split(',').map((frequency) => {
            return {
              value: frequency,
              label: `Deliver Every ${frequency} ${this.subscriptionUnitType}`,
            };
          });
        }

        return [];
      },
      price() {
        return this.currentVariant ? this.currentVariant.price : 0;
      },
      subscriptionDiscount () {
        // Try to get discount from selling plans first
        if (this.product && this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0) {
          const firstPlan = this.product.selling_plan_groups[0].selling_plans[0];
          if (firstPlan && firstPlan.price_adjustments && firstPlan.price_adjustments.length > 0) {
            const adjustment = firstPlan.price_adjustments[0];
            if (adjustment.value_type === 'percentage') {
              return adjustment.value;
            }
          }
        }
        // Fallback to legacy subscription discount
        if (this.product && this.product.subscription) {
          let percentOff = parseInt(this.product.subscription.discount_percentage, 10);
          return percentOff < 100 && percentOff >= 0 ? percentOff : 0;
        }
        return 0;
      },
      subscriptionId () {
        return (this.hasSubscription && this.product && this.product.subscription) ? this.product.subscription.subscription_id : '';
      },
      subscriptionPrice () {
        // For products with selling plans, Shopify will handle the price automatically
        // We just need to show the discount percentage for display purposes
        const discount = this.subscriptionDiscount;
        if (discount > 0 && this.currentVariant) {
          // Simple calculation for display only - actual price comes from Shopify at checkout
          return Math.round(this.currentVariant.price * (1 - discount / 100));
        }
        return this.currentVariant ? this.currentVariant.price : 0;
      },
      selectedSellingPlanId () {
        // For selling plans, selectedFrequency contains the plan ID directly
        if (this.product && this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0) {
          if (this.selectedFrequency) {
            // selectedFrequency is already the selling plan ID for selling plan products
            const planId = parseInt(this.selectedFrequency);
            if (!isNaN(planId)) {
              return planId;
            }
          }
          
          // Fallback to first plan if no match found
          const sellingPlans = this.product.selling_plan_groups[0].selling_plans;
          return sellingPlans[0].id;
        }
        return null;
      },
      // variant data
      hasVariants () {
        return (this.product) ? this.product.variants.length > 1 : false;
      },
      hasComplexVariants () {
        return (this.product) ? this.product.options.length > 1 : false;
      },
      currentVariant () {
        let currentlySelectedVariant;

        if (this.hasComplexVariants) {
          currentlySelectedVariant = this.product.variants.find(variant =>
            variant.option1 === this.selectedSize && variant.option2 === this.selectedGrind);
        } else if (this.hasVariants) {
          currentlySelectedVariant = this.product.variants.find(variant =>
            variant.option1 === this.selectedSize);
        }

        if (currentlySelectedVariant) {
          return currentlySelectedVariant;
        }

        // if we're still here... return the first variant
        return this.product.variants[0];
      },
      currentVariantId () {
        return this.currentVariant ? this.currentVariant.id : null;
      },
      currentVariantIsAvailable () {
        return this.currentVariant ? this.currentVariant.available : false;
      },
      remainingQuantity () {
        return this.currentVariant ? this.currentVariant.remaining_quantity : 0;
      },
    },
    mounted () {
      let interval = setInterval(() => {
        if (this.product) {
          clearInterval(interval);
          this.setDefaultOption();
          this.$store.dispatch('productPage/currentPrice', this.product.price);
        }
      }, 100);

      if (this.hasSubscription && this.subscriptionFrequencies && this.subscriptionFrequencies.length > 0) {
        this.selectedFrequency = this.subscriptionFrequencies[0].value;
      }

      if (this.defaultToSubscription === true) {
        this.selectedPurchaseType = purchaseTypeRecurring;
      }
    },
    watch: {
      currentVariantId () {
        this.$store.dispatch('productPage/selectVariant', this.currentVariantId);
        this.setProductPrice();
      },
      selectedPurchaseType (newVal, oldVal) {
        console.log('ProductForm selectedPurchaseType changed from', oldVal, 'to', newVal);
        this.setProductPrice();
        
        // Set default frequency when switching to subscription
        if (newVal === purchaseTypeRecurring && this.subscriptionFrequencies && this.subscriptionFrequencies.length > 0) {
          if (!this.selectedFrequency) {
            this.selectedFrequency = this.subscriptionFrequencies[0].value;
          }
        }
      }
    },
    methods: {
      setProductPrice() {
        const price = this.isSubscriptionSelected ? this.subscriptionPrice : this.price;
        this.$store.dispatch('productPage/currentPrice', price);
        this.$emit('productVariantPrice', {
          productId: this.product.id,
          price,
        });
      },
      addToCart () {
        let addProduct = {
          id: this.currentVariant.id,
          quantity: this.quantity,
        };

        this.adding = true;

        if (this.isSubscriptionSelected) {
          // Use selling plan if available
          if (this.selectedSellingPlanId) {
            addProduct.selling_plan = this.selectedSellingPlanId;
            // Don't need properties when using selling plans - Shopify handles it
          }
          // No fallback to legacy subscription variants - all products should have selling plans
        }

        const dispatched = this.$store.dispatch('cart/addItem', addProduct)
          .then(() => {
            this.$store.dispatch('cart/openSidecart');
          })
          .catch(error => {
            this.$store.dispatch('toast/send', { text: error, type: 'error' })
          })
          .then(() => {
            this.adding = false;
          });
      },
      buildOptionsArray (optionName) {
        const options = this.product.variants.reduce((options, variant) => {
          if (!options.includes(variant[optionName])) options.push(variant[optionName]);
          return options;
        }, []);
        return options.map((option) => {
          return {
            label: option,
            value: option,
          };
        });
      },
      setDefaultOption () {
        const variant = (this.variantParam)
          ? this.product.variants.find((variant) => variant.id === parseInt(this.variantParam, 10))
          : this.product.variants[0];

        if (this.$refs.variantSelector) {
          this.$refs.variantSelector.update(variant.option1);
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .product-form {
    margin-top: 0;

    @include desktop-up {
      margin-top: $grid-gutter;
    }

    &__atc {
      margin-top: 16px;
      display: flex;
    }

    &__label,
    & select {
      margin-bottom: 0;
      font-size: rem(13);
      line-height: 1.5;
      font-family: $font-gibson;
      color: $color-charcoal;
    }

    &__free-shipping {
      text-align: center;
      font-size: rem(14);
      letter-spacing: 3px;
      line-height: 1.28;
      color: $color--primary;
      text-transform: uppercase;
      margin: $grid-gutter 0 0;
      font-weight: $font-weight--bold;

      &::v-deep .icon {
        margin-right: 14px;
        vertical-align: middle;
        fill: $color--primary;
      }
    }

    .product-form__atc ::v-deep .button {
      min-width: 0;
    }
  }

  .product-form__tile-options {
    margin-bottom: 8px;
  }
</style>
